import React, { useMemo, useCallback, useLayoutEffect, useRef, useEffect } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import ListEmptyView from 'design-system/ListEmptyView';
import IconFilterBold from 'design-system/icons/IconFilterBold';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import { AppView, Button, Indicator, withAnimatedNavigationHeader } from 'design-system';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectIsLoadingCorporateEvents, selectCorporateEventsFlashListData, selectCorporateEventsInitialLoading, } from '../reducers/selectors';
import { getCorporateEvents } from '../actions';
import CorporateEventsSectionRow from '../components/CorporateEventsSectionRow';
import CorporateEventsFilterModal from '../components/CorporateEventsFilterModal';
import { stockScreenDefaultEventTypes } from '../constants';
import { isWeb } from '../../../constants';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const getItemType = (item) => {
    if ('sectionTitle' in item) {
        return 'sectionHeader';
    }
    if ('lastElement' in item) {
        return 'lastElement';
    }
    return 'row';
};
const keyExtractor = (_item, index) => String(index);
const initialState = {
    SPLIT: true,
    MERGER: true,
    HOLIDAY: true,
    DIVIDEND: true,
    EX_DIVIDEND: true,
    SYMBOL_CHANGE: true,
    LISTING_CHANGE: true,
};
const initialStateForStock = {
    SPLIT: true,
    MERGER: true,
    HOLIDAY: false,
    DIVIDEND: true,
    EX_DIVIDEND: true,
    SYMBOL_CHANGE: true,
    LISTING_CHANGE: true,
};
const CorporateEventsScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    const { assetIds, forPortfolio } = route.params;
    const shouldLoadSecondPage = useRef(false);
    const selectorKey = useMemo(() => {
        if (assetIds.length === 1 && !forPortfolio) {
            return assetIds[0];
        }
        return 'portfolio';
    }, [assetIds, forPortfolio]);
    const [state, setState] = React.useState(selectorKey === 'portfolio' ? initialState : initialStateForStock);
    const flashListData = useAppSelector((state) => selectCorporateEventsFlashListData(state, selectorKey));
    const initialLoading = useAppSelector((state) => selectCorporateEventsInitialLoading(state, selectorKey));
    const loading = useAppSelector((state) => selectIsLoadingCorporateEvents(state, selectorKey));
    useEffect(() => {
        if (!initialLoading) {
            dispatch(getCorporateEvents(assetIds, selectorKey, undefined, selectorKey === 'portfolio' ? undefined : stockScreenDefaultEventTypes));
        }
        // Runs only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const mountedRef = useRef(false);
    const types = useMemo(() => {
        const result = [];
        if (state.SPLIT) {
            result.push('SPLIT');
        }
        if (state.MERGER) {
            result.push('MERGER');
        }
        if (state.HOLIDAY) {
            result.push('HOLIDAY');
        }
        if (state.DIVIDEND) {
            result.push('DIVIDEND');
        }
        if (state.EX_DIVIDEND) {
            result.push('EX_DIVIDEND');
        }
        if (state.SYMBOL_CHANGE) {
            result.push('SYMBOL_CHANGE');
        }
        if (state.LISTING_CHANGE) {
            result.push('LISTING_CHANGE');
        }
        if (result.length === 7 || result.length === 0) {
            return undefined;
        }
        return result;
    }, [state]);
    useEffect(() => {
        if (mountedRef.current) {
            dispatch(getCorporateEvents(assetIds, selectorKey, undefined, types));
        }
        else {
            mountedRef.current = true;
        }
    }, [assetIds, selectorKey, types]);
    const loadMoreData = useCallback(() => {
        if (flashListData.futureToken) {
            dispatch(getCorporateEvents(assetIds, selectorKey, flashListData.futureToken, types));
        }
    }, [assetIds, flashListData.futureToken, selectorKey, types]);
    useEffect(() => {
        if (!loading && shouldLoadSecondPage.current) {
            shouldLoadSecondPage.current = false;
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const onLoadNextPage = useCallback(() => {
        if (loading) {
            shouldLoadSecondPage.current = true;
        }
        else {
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const onSetFilter = useCallback((newFilter) => {
        setState(newFilter);
    }, []);
    const onFilterPress = useCallback(() => {
        CorporateEventsFilterModal.show(state, onSetFilter);
    }, [onSetFilter, state]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<NavigationHeaderRightIconButton onPress={onFilterPress}>
            <IconFilterBold color={colors.text.primary} dot={!!types}/>
          </NavigationHeaderRightIconButton>),
        });
    }, [colors.text.primary, onFilterPress, types]);
    const clearFilters = useCallback(() => {
        setState(initialState);
    }, []);
    const renderItem = useCallback(({ item }) => <CorporateEventsSectionRow item={item}/>, []);
    const content = useMemo(() => {
        if (initialLoading && !flashListData.dataLength) {
            return <Indicator dark/>;
        }
        if (flashListData.dataLength === 0) {
            return (<View style={[paddingHorizontalStyle, styles.listEmptyContainer]}>
            <ListEmptyView onCard title="No events found" subTitle={types ? 'To get results, try changing your filters.' : ''}>
              {!!types && <Button title="Clear filters" gray hug style={styles.clearBtn} onPress={clearFilters}/>}
            </ListEmptyView>
          </View>);
        }
        return (<AnimatedFlashList renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} getItemType={getItemType} data={flashListData.events} keyExtractor={keyExtractor} onEndReachedThreshold={0.8} onEndReached={onLoadNextPage} extraData={colors.text.primary} contentContainerStyle={paddingHorizontalStyle} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} initialScrollIndex={flashListData.initialScrollIndex}/>);
    }, [
        initialLoading,
        flashListData.dataLength,
        flashListData.events,
        flashListData.initialScrollIndex,
        renderItem,
        scrollHandler,
        onLoadNextPage,
        colors.text.primary,
        paddingHorizontalStyle,
        styles.listEmptyContainer,
        styles.clearBtn,
        types,
        clearFilters,
    ]);
    return <AppView withFrame={false}>{content}</AppView>;
}, ({ colors, route }) => ({
    title: `${route.params?.stockName || 'Corporate'} events`,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        backgroundColor: colors.cards.onDark,
    },
    listEmptyContainer: {
        paddingTop: rem(16),
        flexDirection: 'row',
    },
    clearBtn: {
        paddingHorizontal: rem(22),
    },
}));
export default CorporateEventsScreen;
